import React, { useLayoutEffect, useRef, useState } from 'react';
import { isLongInactive } from '../utils/inactivity';

// Same palette as HighPriceWarningPopover: gold border, cream fill.
const GOLD = '#C4973A';
const CREAM = '#FFFBF5';

/**
 * Heads-up shown just above an offer form's Send button when the person
 * receiving the offer hasn't been on PedalxChange for a while (see
 * utils/inactivity.js for the rule). Sets expectations; never blocks the
 * offer. Inline rather than a floating popover so it can't drift or be
 * dismissed by scrolling on phones. No dismiss button (removed at the
 * user's request, 2026-10-06): it only ever closed the note.
 *
 * Renders nothing when the owner is active, or when we don't know.
 *
 * `anchorRef` (optional) is the Send button the tail should point at. The
 * buttons aren't centered under the notice on every page (DirectOffer and
 * PurchaseOffer pair Submit with Cancel), so the tail is placed at the
 * button's measured center, re-measured on resize and layout changes, and
 * kept inside the notice's rounded corners. Without an anchor it centers.
 */
const TAIL_MARGIN = 18; // keep the tail clear of the 12px corner radius

const InactiveOwnerNotice = ({ ownerName, lastActiveWeek, now, anchorRef }) => {
  const [tailLeft, setTailLeft] = useState(null); // px from the notice's left edge
  const boxRef = useRef(null);
  const show = isLongInactive(lastActiveWeek, now);

  useLayoutEffect(() => {
    if (!show) return undefined;
    const place = () => {
      const box = boxRef.current;
      const anchor = anchorRef?.current;
      if (!box || !anchor) { setTailLeft(null); return; }
      const b = box.getBoundingClientRect();
      const a = anchor.getBoundingClientRect();
      if (!b.width || !a.width) { setTailLeft(null); return; }
      const center = a.left + a.width / 2 - b.left;
      setTailLeft(Math.min(Math.max(center, TAIL_MARGIN), b.width - TAIL_MARGIN));
    };
    place();
    window.addEventListener('resize', place);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(place);
      if (boxRef.current) ro.observe(boxRef.current);
      if (anchorRef?.current) ro.observe(anchorRef.current);
    }
    return () => {
      window.removeEventListener('resize', place);
      if (ro) ro.disconnect();
    };
  }, [show, anchorRef]);

  if (!show) return null;
  const who = ownerName && ownerName.trim() ? ownerName.trim() : 'This member';
  const tailX = tailLeft == null ? '50%' : `${tailLeft}px`;

  return (
    <div
      ref={boxRef}
      role="status"
      data-testid="inactive-owner-notice"
      style={{
        position: 'relative',
        maxWidth: '420px',
        margin: '0 auto 14px',
        backgroundColor: CREAM,
        border: `1.5px solid ${GOLD}`,
        borderRadius: 12,
        padding: '12px 14px',
        fontFamily: 'Nunito, sans-serif',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.45 }}>
        <strong>Heads up.</strong> {who} hasn&rsquo;t been on PedalxChange in over a month.
        They may not see your offer or respond.
      </p>
      {/* Tail pointing down at the Send button */}
      <span aria-hidden="true" data-testid="inactive-owner-notice-tail" style={{ position: 'absolute', left: tailX, bottom: -9, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: `8px solid ${GOLD}` }} />
      <span aria-hidden="true" style={{ position: 'absolute', left: tailX, bottom: -6, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `6px solid ${CREAM}` }} />
    </div>
  );
};

export default InactiveOwnerNotice;
